import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, CalendarClock, CheckCheck, CircleDollarSign, FileText, Flag, HandCoins, Inbox, ListTodo, Mail,
  MessageCircleQuestion, Pause, Phone, ReceiptText, Send, Sparkles } from 'lucide-react';
import { api, ApiError, formatMoney, type CustomerDeskView } from '../api';
import { DeskSection, EmptyNote, formatMoment } from './desk';
import { Button } from './ui/button';

/** Customer 360 pieces: Amy's brief, the payment habit, the unified timeline and a read-only Ask, all for one customer. */
type Behavior = CustomerDeskView['behavior'];
type Event = CustomerDeskView['timeline'][number];
export interface Brief { source: 'model' | 'basic'; lines: string[]; key: string }

/** Only links a person may safely follow: in-app routes, same-origin paths and plain https. */
export function safeHref(href: string | null | undefined): string | null {
  if (!href) return null;
  if (href.startsWith('#') || /^\/(?![/\\])/.test(href)) return href;
  try { return new URL(href).protocol === 'https:' ? href : null; } catch { return null; }
}
export function scrollToInvoice(invoiceId: string) {
  const row = document.getElementById(`invoice-row-${invoiceId}`);
  if (!row) return;
  row.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  row.classList.remove('flash'); void row.offsetWidth; row.classList.add('flash');
  row.querySelector<HTMLElement>('a, [tabindex]')?.focus({ preventScroll: true });
}

export function AmyBrief({ brief, persona, error }: { brief: Brief | null; persona: string; error: string }) {
  return <section className="amy-brief" aria-label={`${persona}’s read on this customer`} aria-busy={!brief && !error}>
    <span className="amy-brief-mark" aria-hidden><Sparkles size={17} /></span>
    <div className="amy-brief-body">
      <h2>{persona}’s read{brief && <small>{brief.source === 'model' ? 'Checked against the ledger' : 'Basic summary (AI unavailable)'}</small>}</h2>
      {/* One sentence per line: how they pay, where it stands, the plan. */}
      {brief ? <ul className="amy-brief-lines">{brief.lines.map((line, i) => <li key={i}>{line}</li>)}</ul>
        : error ? <p className="muted">{error}</p>
        : <div className="amy-brief-loading" role="status"><span /><span />
          <span className="sr-only">{persona} is reading this account</span></div>}
    </div>
  </section>;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const lateness = (days: number) =>
  days > 0 ? `${plural(days, 'day')} late` : days < 0 ? `${plural(-days, 'day')} early` : 'on the due date';
const bucket = (days: number) => days <= 0 ? 'current' : days <= 30 ? '1-30' : days <= 60 ? '31-60' : days <= 90 ? '61-90' : '90plus';

/** Each paid invoice as a mark on one days-late axis, with the median and the late tail drawn over it. */
function LatenessStrip({ behavior }: { behavior: Behavior }) {
  const days = behavior.paid.map(p => p.daysLate);
  const lo = Math.min(0, ...days); const hi = Math.max(lo + 14, ...days);
  const at = (d: number) => `${((d - lo) / (hi - lo)) * 100}%`;
  const stack = new Map<number, number>();
  const label = `${plural(days.length, 'paid invoice')}, from ${lateness(Math.min(...days))} to ${lateness(Math.max(...days))}; `
    + `typically ${lateness(behavior.medianDaysLate ?? 0)}.`;
  return <figure className="lateness" aria-label={label}>
    <div className="lateness-track" role="img" aria-label={label}>
      <span className="lateness-due" style={{ left: at(0) }} />
      {behavior.p75DaysLate !== null && <span className="lateness-tail" style={{ left: at(behavior.medianDaysLate ?? 0),
        width: `calc(${at(behavior.p75DaysLate)} - ${at(behavior.medianDaysLate ?? 0)})` }} />}
      {behavior.paid.map(p => {
        const level = stack.get(p.daysLate) ?? 0; stack.set(p.daysLate, level + 1);
        return <i key={p.number} className={`b-${bucket(p.daysLate)}`}
          style={{ left: at(p.daysLate), bottom: `${6 + Math.min(level, 4) * 7}px` }}
          title={`${p.number}: paid ${p.paidOn}, ${lateness(p.daysLate)}${p.payments > 1 ? `, in ${p.payments} payments` : ''}`} />;
      })}
      {behavior.medianDaysLate !== null && <span className="lateness-median" style={{ left: at(behavior.medianDaysLate) }}>
        <b>Typical</b></span>}
    </div>
    <figcaption><span style={{ left: at(0) }}>Due date</span><span>{hi} days late</span></figcaption>
  </figure>;
}

export function PaymentHabit({ behavior }: { behavior: Behavior }) {
  const aside = <small className={`habit ${behavior.hint.kind}`}>{behavior.hint.label}</small>;
  if (!behavior.enoughHistory) return <DeskSection title="Payment habit" aside={aside}>
    <EmptyNote icon={<CalendarClock size={20} aria-hidden />} title="Not enough history yet">
      {behavior.paidInvoices ? `${plural(behavior.paidInvoices, 'paid invoice')} so far. ` : 'No paid invoices yet. '}
      A pattern needs at least three.</EmptyNote>
  </DeskSection>;
  const trend = behavior.trend;
  return <DeskSection title="Payment habit" aside={aside} className="habit-section">
    <LatenessStrip behavior={behavior} />
    <dl className="habit-figures">
      <div><dt>Typically pays</dt><dd>{lateness(behavior.medianDaysLate!)}</dd></div>
      <div><dt>Late tail, 1 in 4 invoices</dt>
        <dd>{behavior.p75DaysLate! > 0 ? `${behavior.p75DaysLate} days or more` : 'On time'}</dd></div>
      <div><dt>Paid on time</dt><dd>{behavior.onTimePercent}%</dd></div>
      <div><dt>Pays in parts</dt><dd>{behavior.partial.habit ? 'Often' : behavior.partial.paidInParts ? 'Sometimes' : 'Rarely'}
        {behavior.partial.openPartlyPaid > 0
          && <small>{plural(behavior.partial.openPartlyPaid, 'open invoice')} part paid</small>}</dd></div>
      {behavior.reminderToPayment.samples >= 2 && <div><dt>After a reminder</dt>
        <dd>Pays in ~{plural(behavior.reminderToPayment.medianDays!, 'day')}</dd></div>}
      {trend && <div><dt>Trend</dt><dd className={`trend ${trend.direction}`}>{trend.direction === 'steady' ? 'Steady'
        : `${trend.direction === 'slipping' ? 'Slipping' : 'Improving'}: `
          + `${trend.olderMedianDaysLate} → ${trend.recentMedianDaysLate} days`}</dd></div>}
    </dl>
    <p className="habit-basis">Based on {plural(behavior.paidInvoices, 'paid invoice')} in the synced history.</p>
  </DeskSection>;
}

const categoryLabels: Record<string, string> = {
  invoices: 'Invoices', payments: 'Payments', emails: 'Amy’s emails', calls: 'Calls', documents: 'Documents sent', reminders: 'Sequence reminders', replies: 'Replies',
  promises: 'Promises', holds: 'Holds', tasks: 'Tasks', decisions: 'Decisions',
};
const icons: Record<string, ReactNode> = {
  invoice_issued: <FileText size={14} aria-hidden />, invoice_due: <CalendarClock size={14} aria-hidden />,
  email_sent: <Send size={14} aria-hidden />, email_shadow: <Send size={14} aria-hidden />, draft: <Mail size={14} aria-hidden />,
  external_reminder: <Mail size={14} aria-hidden />, bounced: <AlertTriangle size={14} aria-hidden />,
  reply_received: <Inbox size={14} aria-hidden />, payment: <CircleDollarSign size={14} aria-hidden />,
  credit: <ReceiptText size={14} aria-hidden />, promise: <HandCoins size={14} aria-hidden />, task: <ListTodo size={14} aria-hidden />,
  decision: <CheckCheck size={14} aria-hidden />, reply: <Flag size={14} aria-hidden />, call: <Phone size={14} aria-hidden />,
  documents: <FileText size={14} aria-hidden />,
};
const pageSize = 40;

/** Who moved the work — agent / person / customer / system from the timeline read model (#223). */
function actorLabel(actor: Event['actor'] | undefined, persona: string): string | null {
  if (!actor) return null;
  if (actor === 'agent') return persona;
  if (actor === 'person') return 'Your team';
  if (actor === 'customer') return 'Customer';
  if (actor === 'system') return 'System';
  return null;
}

export function CustomerTimeline({ events, asOf, persona }: { events: Event[]; asOf: string; persona: string }) {
  const [filter, setFilter] = useState<string | null>(null); const [shown, setShown] = useState(pageSize);
  const counts = useMemo(() => events.reduce<Record<string, number>>((m, e) => ({ ...m, [e.category]: (m[e.category] ?? 0) + 1 }), {}),
    [events]);
  const visible = events.filter(e => !filter || e.category === filter);
  const labels: Record<string, string> = { ...categoryLabels, emails: `${persona}’s emails` };
  return <DeskSection title="Timeline" aside={<small className="muted">Newest first</small>} className="timeline-section">
    {events.length > 0 && <div className="timeline-filters" role="group" aria-label="Show only">
      <button type="button" aria-pressed={!filter} onClick={() => { setFilter(null); setShown(pageSize); }}>All</button>
      {Object.keys(categoryLabels).filter(c => counts[c]).map(c => <button key={c} type="button" aria-pressed={filter === c}
        onClick={() => { setFilter(filter === c ? null : c); setShown(pageSize); }}>{labels[c]}<span>{counts[c]}</span></button>)}
    </div>}
    {visible.length ? <ol className="timeline">{visible.slice(0, shown).map(t => {
      const href = safeHref(t.href);
      const who = actorLabel(t.actor, persona);
      const title = href ? <a href={href} {...(href.startsWith('#') ? {} : { target: '_blank', rel: 'noreferrer' })}>{t.title}</a>
        : t.invoiceId ? <button type="button" className="link-button" onClick={() => scrollToInvoice(t.invoiceId!)}>{t.title}</button>
          : t.title;
      return <li key={t.id} className={`${t.kind} ${t.category}`}>
        <span className="timeline-icon">{icons[t.kind] ?? <Pause size={14} aria-hidden />}</span>
        <div><strong>{title}</strong>{t.detail && <p>{t.detail}</p>}
          {t.notes?.map((note, n) => <p key={n} className="muted">{note}</p>)}
          <div className="timeline-meta">
            {who && <span className={`timeline-actor actor-${t.actor}`}>{who}</span>}
            <time dateTime={t.at}>{formatMoment(t.at, asOf)}</time>
          </div></div>
        {t.amount && <span className="timeline-amount">{formatMoney(t.amount.minor, t.amount.currency)}</span>}</li>;
    })}</ol> : <EmptyNote icon={<Mail size={20} aria-hidden />} title="Nothing has happened yet">
      Invoices, emails, replies, approvals, promises and payments collect here.</EmptyNote>}
    {visible.length > shown && <Button variant="ghost" size="sm" className="timeline-more" onClick={() => setShown(shown + pageSize)}>
      Show {Math.min(pageSize, visible.length - shown)} older</Button>}
  </DeskSection>;
}

interface Citation { id: string; type: string; recordId: string; label: string }
interface AnswerBlock { type: string; title: string; text: string; citations: Citation[] }
interface Answer { blocks: AnswerBlock[]; citations: Citation[]; source: string; fallbackReason: string | null }
interface Turn { id: string; question: string; answer: Answer | null; error?: string }
interface Stored { id: string; role: string; text: string }
const suggestions = ['Have they paid anything recently?', 'What did they say last?', 'Why is this the next step?'];

function Sources({ citations, invoiceIds }: { citations: Citation[]; invoiceIds: Set<string> }) {
  // Only records a person recognises by name; a bare record id is never shown.
  const shown = citations.filter(c => ['invoice', 'customer', 'approval', 'reply'].includes(c.type) && c.label !== c.recordId);
  const unique = [...new Map(shown.map(c => [c.id, c])).values()].slice(0, 8);
  if (!unique.length) return null;
  return <p className="ask-sources"><span>Sources</span>{unique.map(c => {
    const id = encodeURIComponent(c.recordId);
    const href = c.type === 'approval' ? `#approvals/${id}` : c.type === 'reply' ? `#inbox/${id}` : null;
    if (href) return <a key={c.id} href={href}>{c.label}</a>;
    if (c.type === 'invoice' && invoiceIds.has(c.recordId)) {
      return <button key={c.id} type="button" onClick={() => scrollToInvoice(c.recordId)}>{c.label}</button>;
    }
    return <span key={c.id}>{c.label}</span>;
  })}</p>;
}

/** Questions about this customer, answered by the same Brain as Slack with the customer pinned. Read-only. */
export function AskAmy({ customerId, customerName, persona, invoiceIds }:
  { customerId: string; customerName: string; persona: string; invoiceIds: Set<string> }) {
  const path = `/app-api/customers/${encodeURIComponent(customerId)}/ask`;
  const [allowed, setAllowed] = useState<boolean | null>(null); const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState(''); const [busy, setBusy] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null); const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    void api<Stored[]>(path).then(history => {
      if (!active) return;
      setAllowed(true);
      const pairs: Turn[] = [];
      for (let i = 0; i + 1 < history.length; i += 2) {
        if (history[i]!.role !== 'user') continue;
        pairs.push({ id: history[i]!.id, question: history[i]!.text, answer: { blocks: [{ type: 'answer', title: persona,
          text: history[i + 1]!.text, citations: [] }], citations: [], source: 'history', fallbackReason: null } });
      }
      setTurns(pairs.slice(-4));
    }).catch((e: unknown) => { if (active) setAllowed(!(e instanceof ApiError && e.status === 403)); });
    return () => { active = false; };
  }, [path, persona]);
  // Follow the conversation only after a question is asked here. Loading earlier turns must not move the page: the box sits
  // near the bottom, so scrolling it into view on load opened every customer ~2,000px down.
  const asked = useRef(false);
  useEffect(() => { if (asked.current) end.current?.scrollIntoView({ block: 'nearest' }); }, [turns]);
  if (allowed === false) return null;
  async function ask(question: string, event?: FormEvent) {
    event?.preventDefault();
    const q = question.trim(); if (!q || busy) return;
    asked.current = true;
    const id = crypto.randomUUID(); setTurns(t => [...t, { id, question: q, answer: null }]); setText(''); setBusy(true);
    try {
      const answer = await askQuestion(path, q);
      setTurns(t => t.map(x => (x.id === id ? { ...x, answer } : x)));
    } catch (e) {
      setTurns(t => t.map(x => (x.id === id ? { ...x, error: e instanceof Error ? e.message : 'The answer could not be completed.' } : x)));
    } finally { setBusy(false); input.current?.focus(); }
  }
  return <DeskSection title={`Ask ${persona}`} aside={<small className="muted">About {customerName} only</small>} className="ask-section">
    <div className="ask-thread" aria-live="polite">
      {!turns.length && <div className="ask-suggestions">{suggestions.map(s =>
        <button key={s} type="button" onClick={() => void ask(s)} disabled={busy}>
          <MessageCircleQuestion size={13} aria-hidden />{s}</button>)}
      </div>}
      {turns.map(t => <div key={t.id} className="ask-turn">
        <p className="ask-question">{t.question}</p>
        {t.error ? <p className="ask-error" role="alert">{t.error}</p> : !t.answer ? <p className="ask-pending" role="status">
          {persona} is reading the records…</p> : <div className="ask-answer">
          {t.answer.blocks.map((b, i) => <div key={i}>
            {b.title !== persona && b.title !== 'Amy' && <strong>{b.title}</strong>}<p>{b.text}</p></div>)}
          <Sources citations={t.answer.citations} invoiceIds={invoiceIds} />
          {t.answer.source === 'fallback' && t.answer.fallbackReason && <small>Basic answer from the records (AI unavailable).</small>}
        </div>}
      </div>)}
      <div ref={end} />
    </div>
    <form className="ask-form" onSubmit={e => void ask(text, e)}>
      <label className="sr-only" htmlFor={`ask-${customerId}`}>Ask {persona} about {customerName}</label>
      <textarea id={`ask-${customerId}`} ref={input} rows={2} maxLength={2000} value={text} placeholder={`Ask about ${customerName}`}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(text); } }} />
      <Button type="submit" size="sm" disabled={busy || !text.trim()}>Ask</Button>
    </form>
    <p className="ask-note">{persona} answers from the ledger and never acts from here.</p>
  </DeskSection>;
}

/** POST without the global refresh event: a question changes nothing, so the page should not reload. */
async function askQuestion(path: string, text: string): Promise<Answer> {
  const csrf = document.cookie.split('; ').find(c => c.startsWith('alder_csrf='))?.split('=').slice(1).join('=');
  const response = await fetch(path, { method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': decodeURIComponent(csrf ?? '') }, body: JSON.stringify({ text }) });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(result.error ?? 'The answer could not be completed. Please retry.', response.status);
  return result as Answer;
}
